import { z } from "zod";
import { narrativeMetadataSchema } from "./metadata.schema.js";
import { narrativeSectionSchema } from "./section.schema.js";

/**
 * Type for data values that will replace placeholders.
 * Interface allows for recursive self-reference in TypeScript 5.9.
 */
export interface DataValues {
  [key: string]: string | number | boolean | DataValues;
}

/**
 * ZOD 4 FIX: z.record now requires 2 arguments in this environment: (keySchema, valueSchema)
 */
export const dataValuesSchema: z.ZodType<DataValues> = z
  .lazy(() =>
    z.record(
      z.string(),
      z.union([
        z.string(),
        z.number(),
        z.boolean(),
        z.lazy(() => dataValuesSchema),
      ]),
    ),
  )
  .describe(
    "Values substituted into {{placeholder}} tokens throughout the narrative. Keys are placeholder names; values are strings, numbers, booleans, or nested objects addressed with dot paths.",
  );

/**
 * Core narrative structure (Phase 1.6+)
 * Contains metadata and sections
 */
export const newNarrativeDataSchema = z
  .object({
    metadata: narrativeMetadataSchema,
    sections: z
      .array(narrativeSectionSchema)
      .min(1)
      .describe("Story chapters in display order (minimum 1)."),
  })
  .describe("Core narrative structure: metadata plus ordered sections.");

/**
 * Narrative with data values (for JSON files)
 * This is the complete structure loaded from narrative JSON files
 */
export const narrativeWithDataValuesSchema = newNarrativeDataSchema
  .extend({
    narrativeDataValues: dataValuesSchema,
  })
  .describe(
    "The complete narrative JSON file structure: metadata, sections, and the data values that fill {{placeholder}} tokens.",
  );

// Export inferred types
export type NewNarrativeData = z.infer<typeof newNarrativeDataSchema>;
export type NarrativeWithDataValues = z.infer<
  typeof narrativeWithDataValuesSchema
>;
